// Example usage:
// const cancel = copyTextOnKeyCombo("Hello Wizard 🧙‍♂️");
// const cancel2 = copyTextOnKeyCombo("Copied!", { combo: "Ctrl+C" });
// cancel();

// --- React Hook ---
// Usage:
// useCopyOnKeyCombo("Hello", { combo: "Alt+Shift+T" });

import { useEffect } from "react";

export interface CopyOnKeyComboOptions {
    /**
     * Key combo like "Ctrl+C", "Alt+Shift+T", "Meta+X"
     * Default: "Alt+Shift+T"
     */
    combo?: string;

    /**
     * Listen on this target instead of window
     */
    target?: EventTarget;

    /**
     * Remove listener after first successful copy (default: true)
     */
    once?: boolean;
}

function parseCombo(combo: string) {
    const parts = combo.toLowerCase().split("+").map(p => p.trim());

    return {
        ctrl: parts.includes("ctrl"),
        alt: parts.includes("alt"),
        shift: parts.includes("shift") || parts.includes("shft"),
        meta: parts.includes("meta") || parts.includes("cmd") || parts.includes("command"),
        key: parts.find(p => !["ctrl", "alt", "shift", "shft", "meta", "cmd", "command"].includes(p)),
    };
}

function matchesCombo(event: KeyboardEvent, combo: ReturnType<typeof parseCombo>): boolean {
    if (combo.ctrl !== event.ctrlKey) return false;
    if (combo.alt !== event.altKey) return false;
    if (combo.shift !== event.shiftKey) return false;
    if (combo.meta !== event.metaKey) return false;

    if (combo.key) {
        return event.key.toLowerCase() === combo.key;
    }

    return true;
}

/**
 * Registers a keydown listener that copies the provided text
 * when the specified key combo is pressed.
 *
 * Returns a cancel function that removes the event listener.
 */
export function copyTextOnKeyCombo(
    text: string,
    options: CopyOnKeyComboOptions = {}
): () => void {
    const {
        combo = "Alt+Shift+T",
        target = window,
        once = true,
    } = options;

    const parsed = parseCombo(combo);

    const handler = async (event: KeyboardEvent): Promise<void> => {
        if (!matchesCombo(event, parsed)) return;

        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }

        if (once) {
            target.removeEventListener("keydown", keydownListener);
        }
    };

    const keydownListener = (event: Event): void => {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }

        void handler(event);
    };

    target.addEventListener("keydown", keydownListener);

    const cancel = (): void => {
        target.removeEventListener("keydown", keydownListener);
    };

    return cancel;
}

export function useCopyOnKeyCombo(
    text: string,
    options: CopyOnKeyComboOptions = {}
): void {
    const { combo = "Alt+Shift+T", target = window, once = true } = options;

    useEffect(() => {
        const cancel = copyTextOnKeyCombo(text, { combo, target, once });
        return () => {
            cancel();
        };
    }, [text, combo, target, once]);
}

